import { z } from 'zod';
// A model as served by a provider — a couple, never an id, and never
// concatenated. `model` is verbatim and may contain a "/" (e.g. an OpenRouter
// model `anthropic/claude-…`). The couple is the barème's pricing key and the
// journal's model dimension.
export const ServedModelSchema = z.object({
    provider: z.string(),
    model: z.string()
});
// Billing subject type. `user` only at launch; `organization` (funded pool) and
// `member` (per-seat cap) come with org support. Single source of truth, reused
// by the storage layer, the billing scope resolver, and the wire schemas — and
// enforced at the DB level by a CHECK constraint (migration 021).
export const ScopeTypeSchema = z.enum(['user', 'organization', 'member']);
// Billing posture for a subject. Set per plan by the app / back-office; shared
// so the admin UI and the server agree on the contract. Not a degree of
// severity — two postures: we measure either way, we only refuse calls in one.
//   'observe' — measure + price, but never refuse a call (the balance can go
//               exhausted; we can see who would be blocked, we don't act on it).
//   'enforce' — refuse a call when the subject is exhausted OR has no account.
export const BillingModeSchema = z.enum(['observe', 'enforce']);
// Per-call token counts, normalised across providers, as DISJOINT leaves
// (mirrors the AI SDK's input/outputTokenDetails). Totals are derivable —
// inputTokens = input.uncached + input.cacheRead + input.cacheWrite ;
// outputTokens = output.text + output.reasoning — so a barème is a plain
// weighted sum with no double-counting. This is the barème's input.
export const TokenUsageSchema = z.object({
    input: z.object({
        uncached: z.number().int().nonnegative(), // prompt tokens not served from cache
        cacheRead: z.number().int().nonnegative(), // prompt tokens read from cache (usually discounted)
        cacheWrite: z.number().int().nonnegative() // prompt tokens written to cache
    }),
    output: z.object({
        text: z.number().int().nonnegative(), // completion text tokens
        reasoning: z.number().int().nonnegative() // completion reasoning tokens
    })
});
// Aggregated LLM token usage for a subject, broken down by call kind
// (message / title / summary). Same disjoint breakdown as TokenUsage, plus the
// kind and call count. Wire shape returned by the admin usage endpoint.
export const UsageByKindSchema = TokenUsageSchema.extend({
    kind: z.string(),
    callCount: z.number().int().nonnegative()
});
export const UserUsageSchema = z.object({
    userId: z.string(),
    usage: z.array(UsageByKindSchema)
});
// Rolled-up credit balance of a subject's active lots. `available` and the
// implicit floor may be negative once overrun is enabled.
export const BalanceSchema = z.object({
    granted: z.number().int().nonnegative(),
    consumed: z.number().int().nonnegative(),
    available: z.number().int(),
    blocked: z.boolean()
});
/**
 * A user's credits over the period in course, as their settings show them:
 * the plan's envelopes, then the top-ups. Stored sub-units, like a lot.
 */
export const UserCreditsSchema = z.object({
    // The period in course of the subscription in force.
    period: z.object({ startsAt: z.string(), endsAt: z.string() }),
    // The plan's envelopes for the period — two after a move up mid-period.
    planCredits: z.object({
        granted: z.number().int().nonnegative(),
        consumed: z.number().int().nonnegative()
    }),
    // Top-ups: what was drawn from them since the period started — the use
    // beyond the plan — and what is left. Absent for a user who has none.
    recharges: z.object({
        consumedThisPeriod: z.number().int().nonnegative(),
        remaining: z.number().int()
    }).optional(),
    // Whether calls are refused now, as the gate decides: never in 'observe',
    // and in 'enforce' without credits or past them.
    blocked: z.boolean()
});
export const CreditLotSourceSchema = z.enum(['base', 'recharge']);
// A credit envelope on the wire (ISO date strings).
export const CreditLotSchema = z.object({
    id: z.string(),
    scopeType: ScopeTypeSchema,
    scopeId: z.string(),
    source: CreditLotSourceSchema,
    creditsGranted: z.number().int().nonnegative(),
    creditsConsumed: z.number().int().nonnegative(),
    validFrom: z.string(),
    validUntil: z.string().optional(),
    planRef: z.string().optional(),
    reason: z.string().optional(),
    clientToken: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string()
});
// Body of POST /admin/billing/lots: create an included (base) or recharge lot
// for a scope. Credits are a positive integer in the app's fine sub-unit.
//
// `validFrom` / `validUntil` MUST be absolute ISO-8601 instants **with an
// offset** (e.g. `2026-06-01T00:00:00+02:00` or `...Z`), never a bare date —
// core-api knows no timezone and treats them as absolute points in time. The
// caller resolves what "the 5th at midnight" means before saying it here.
//
// This endpoint grants a lot directly, which is how a recharge and a
// back-office correction are posted. A subscription's own included lots are
// not created this way: they come from the renewal job, which reads the
// subject's subscription.
export const CreateLotRequestSchema = z.object({
    scopeType: ScopeTypeSchema,
    scopeId: z.string(),
    source: CreditLotSourceSchema,
    creditsGranted: z.number().int().positive(),
    validFrom: z.string(),
    validUntil: z.string().optional(),
    planRef: z.string().optional(),
    reason: z.string().optional(),
    clientToken: z.string().optional()
});
